import { Box, Button, Chip, CircularProgress, Grid, Stack } from '@mui/material';
import { useFormik } from 'formik';
import React, { useState } from 'react';
import { TbEdit } from 'react-icons/tb';
import { Cell } from 'react-table';
import {
  useCreateTrainingMutation,
  useCurrentUserDetailsQuery,
  useFetchTrainingDetailQuery,
  useUpdateTrainingDetailMutation
} from 'redux/reducers/user/dashboard/tms-user';
import TextInput from '../components/TextInput';
import { FIELD_SIZE, Section, StickyActions } from '../components/Section';
import { ITraining, trainingInitialValues, TrainingValidationSchema } from './Schema';
// import Datatable from '@tms/components/Datatable';
import DataTable from 'components/ui/DataTable/data-table';
import { toast } from 'react-toastify';

const Trainings = ({
  activeStep,
  setActiveStep
}: {
  activeStep: number;
  setActiveStep: (step: number) => void;
}) => {
  const { data: CurrentUser, isLoading } = useCurrentUserDetailsQuery();
  const TraineeId = CurrentUser?.id;
  const [trainingId, setTrainingId] = useState<string>('');

  const { mutateAsync: createTraining, isLoading: isCreating } = useCreateTrainingMutation(
    TraineeId ?? ''
  );

  const { mutateAsync: updateTraining, isLoading: isUpdating } = useUpdateTrainingDetailMutation(
    TraineeId ?? '',
    trainingId ?? ''
  );

  const { data: TrainingDetail, isLoading: isTrainingDetailLoading } = useFetchTrainingDetailQuery(
    TraineeId ?? ''
  );

  const DataForEdit: ITraining[] = TrainingDetail?.data?.filter(
    (train: any) => train.id == trainingId
  );
  React.useEffect(() => {
    if (trainingId) {
      formik.resetForm({
        values: {
          work_train_id: DataForEdit[0]?.work_train_id?.toString() ?? '',
          name: DataForEdit[0]?.name ?? '',
          from_date: DataForEdit[0]?.from_date ?? '',
          to_date: DataForEdit[0]?.to_date ?? '',
          organization: DataForEdit[0]?.organization ?? ''
        }
      });
    }
  }, [trainingId]);
  const cols = [
    {
      Header: 'Training Name',
      accessor: 'name'
    },
    { Header: 'Organization', accessor: 'organization' },
    { Header: 'From Date', accessor: 'from_date' },
    { Header: 'To Date', accessor: 'to_date' },
    {
      Header: 'Status',
      Cell: (cell: Cell<any>) => (
        <TbEdit
          fontSize={22}
          style={{ cursor: 'pointer' }}
          onClick={(_) => {
            setTrainingId(cell?.row?.original?.id);
          }}
        />
      )
    }
  ];
  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: TrainingValidationSchema,
    initialValues: trainingInitialValues,

    onSubmit: (values) => {
      let executeFunc = createTraining;
      if (trainingId) {
        executeFunc = updateTraining;
      }

      if (trainingId) {
        formik.resetForm({ values: trainingInitialValues });
        executeFunc({ ...values, work_train_id: trainingId })
          .then(() => {
            toast.success('Updated successfully');
            setTrainingId('');
          })
          .catch((err) => {
            toast.warn(err?.response?.data[0]);
          });
      } else {
        const { work_train_id, ...sendValue } = values;
        executeFunc(sendValue)
          .then(() => {
            toast.success('Added successfully');
            formik.resetForm();
          })
          .catch((err) => {
            toast.warn(err?.response?.data[0]);
          });
      }
    }
  });

  const isEditing = !!trainingId;
  const isSaving = isUpdating || isCreating || formik.isSubmitting;

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 320 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <Section
        title={isEditing ? 'Edit Training' : 'Add Training'}
        description="Record the trainings you have attended, one entry at a time."
        action={isEditing && <Chip label="Editing entry" color="primary" size="small" />}
      >
        <TextInput
          {...FIELD_SIZE}
          label="Training Name"
          name="name"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.name}
          onBlur={formik.handleBlur}
          formik={formik}
          required
        />
        <TextInput
          {...FIELD_SIZE}
          label="From Date"
          name="from_date"
          type="date"
          onChange={formik.handleChange}
          value={formik.values.from_date}
          formik={formik}
        />
        <TextInput
          {...FIELD_SIZE}
          label="To Date"
          name="to_date"
          type="date"
          onChange={formik.handleChange}
          value={formik.values.to_date}
          formik={formik}
        />
        <TextInput
          {...FIELD_SIZE}
          label="Training Organization"
          name="organization"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.organization}
          onBlur={formik.handleBlur}
          formik={formik}
          required
        />

        <Grid item xs={12}>
          <Stack direction="row" justifyContent={{ xs: 'stretch', sm: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress color="inherit" size={16} /> : undefined}
              sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 160 }}
            >
              {isEditing ? 'Edit & Submit' : 'Add & Submit'}
            </Button>
          </Stack>
        </Grid>
      </Section>

      <Section
        title="Training History"
        description="Trainings you have already saved. Use the edit icon to load one back into the form."
      >
        <Grid item xs={12}>
          <Box sx={{ overflowX: 'auto' }}>
            <DataTable
              data={TrainingDetail?.data || []}
              columns={cols}
              isLoading={isTrainingDetailLoading}
              hidePagination={true}


            />
          </Box>
        </Grid>
      </Section>

      {activeStep > 1 && (
        <StickyActions>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setActiveStep(--activeStep)}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Previous Step
          </Button>
        </StickyActions>
      )}
    </form>
  );
};

export default Trainings;
